import React, { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";
import { Box, CircularProgress, Typography, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Environment } from "../utils/Environment";
import Layout from "./Layout";
import { useAuth } from "../utils/AuthContext";
import Button from "@mui/material/Button";


const apiUrl = Environment.getApiUrl();

const TABLE_OPTIONS = [
    "base dataset",
    "flowline",
    "structures",
    "wetlands",
    "developed",
    "concrete",
    "steel",
    "allWater",
    "roadway",
    "railway"
];

const DisplayTableToUser: React.FC = () => {
    const [selectedTable, setSelectedTable] = useState(TABLE_OPTIONS[0]);
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { isLoggedIn, userName, logout } = useAuth();

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const client_id = localStorage.getItem("client_id") || "";
        const job_id = localStorage.getItem("job_id") || "";
        const isBaseTable = selectedTable === "base dataset";

        fetch(`${apiUrl}/user-table`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                client_id,
                job_id,
                table: selectedTable,
                isBaseTable,
            }),
        })
            .then(async res => {
                if (!res.ok) {
                    let errMsg = "Failed to load table data.";
                    try {
                        const errorJson = await res.json();
                        if (errorJson.detail) {
                            errMsg = errorJson.detail;
                        }
                    } catch {
                        // fallback to text if not JSON
                        errMsg = await res.text() || errMsg;
                    }
                    throw new Error(errMsg);
                }
                return res.json();
            })
            .then(json => {
                // Ensure data is always an array
                setData(Array.isArray(json) ? json : []);
                setLoading(false);
            })
            .catch(err => {
                let msg = err.message || "";
                if (!msg.includes("Please check if table was uploaded.")) {
                    msg += " Please check if table was uploaded.";
                }
                setError(msg);
                setData([]);
                setLoading(false);
            });
    };

    const columns: GridColDef[] =
        data.length > 0
            ? Object.keys(data[0]).map(key => ({
                field: key,
                headerName: key.charAt(0).toUpperCase() + key.slice(1),
                width: 150,
            }))
            : [];

    return (
        <Layout isLoggedIn={isLoggedIn} onLogout={logout} userName={userName}>
            <Box sx={{ width: "100%", maxWidth: "100vw", p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Your Uploaded Table
                </Typography>
                <form onSubmit={handleSubmit}>
                    <FormControl sx={{ mb: 2, minWidth: 220 }}>
                        <InputLabel id="table-select-label">Select Table</InputLabel>
                        <Select
                            labelId="table-select-label"
                            value={selectedTable}
                            label="Select Table"
                            onChange={e => setSelectedTable(e.target.value)}
                        >
                            {TABLE_OPTIONS.map(option => (
                                <MenuItem key={option} value={option}>
                                    {option.charAt(0).toUpperCase() + option.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: "inline-block", ml: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            sx={{ height: 40, mt: 1 }}
                        >
                            Submit
                        </Button>
                    </Box>
                </form>
                {loading ? (
                    <CircularProgress />
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : data.length === 0 ? (
                    <Typography>No data to display.</Typography>
                ) : (
                    <DataGrid
                        rows={data.map((row, idx) => ({ id: idx, ...row }))}
                        columns={columns}
                        sx={{ height: 500, width: "100%" }} // fixed height for DataGrid
                        pageSizeOptions={[10, 25, 50, 100]}
                        hideFooterSelectedRowCount
                    />
                )}
            </Box>
        </Layout>
    );
};

export default DisplayTableToUser;